// Client-side aggregation for the Reporting "Handle Time" chart and stat
// tiles. The server (getHandleTimeReport) returns `agentDaily` at the
// finest grain -- one entry per agent, per day, holding the RAW ms
// durations for that agent/day (not a pre-computed stat) -- specifically
// so this file can pool an arbitrary subset of agents and days and
// recompute a genuinely correct median/average for that combination.
//
// This is NOT the same problem contactsPerHourAggregate.js solves. That
// file re-buckets by SUMMING each day's already-merged activeHours, which
// is safe because hours are additive. Median (and, less obviously,
// average across an uneven agent selection) are NOT additive: a weekly
// median is not the average of that week's 7 daily medians, and two
// agents' separately-computed medians don't combine into their joint
// median. The only correct path is pooling the raw values first, then
// running the stat once over the pooled list -- which is what every
// function below does.
//
// weekEndingOf/percentileOf/durationStats duplicate
// server/src/repositories/reporting.js's weekEndingLabel/percentileOf/
// durationStats -- `ui` has no dependency on that package (or even
// @cxhub/shared, see ui/src/constants/channels.js's module comment for
// the same constraint), so this is a genuine second copy, not a shared
// import. server/test/handle-time-week-sync.test.js is what keeps the
// week-ending copy honest; the percentile copy is a small, static
// nearest-rank formula pinned by ui/test/handleTimeAggregate.test.js's own
// expected-output assertions.

function ymd(date) {
  return date.toISOString().slice(0, 10)
}

// The Sunday-Saturday week-ending-Saturday label for a day string
// ('YYYY-MM-DD') -- labeled by the ENDING Saturday, the OPPOSITE of this
// file's sibling contactsPerHourAggregate.js's weekStartOf (which labels
// by the STARTING Sunday). Do not conflate the two conventions. Operates
// purely on the already-resolved day label via UTC-midnight arithmetic, so
// it's DST-safe by construction -- same reasoning as the server twin.
//
// Deliberately self-contained (inlines what ymd() does elsewhere in this
// file) rather than calling that helper: server/test/handle-time-week-
// sync.test.js extracts this function's source text and re-evaluates it
// standalone via `new Function`, which would throw a ReferenceError on any
// name this body doesn't define or receive as a parameter.
export function weekEndingOf(day) {
  const d = new Date(`${day}T00:00:00Z`)
  const dow = d.getUTCDay() // 0=Sun..6=Sat
  return new Date(d.getTime() + (6 - dow) * 86400000).toISOString().slice(0, 10)
}

function bucketOf(day, granularity) {
  return granularity === 'week' ? weekEndingOf(day) : day
}

// Nearest-rank percentile over an ALREADY-SORTED-ascending ms array.
function percentileOf(sortedAsc, p) {
  if (!sortedAsc.length) return 0
  const rank = Math.ceil((p / 100) * sortedAsc.length)
  return sortedAsc[Math.min(sortedAsc.length, Math.max(1, rank)) - 1]
}

// Same shape as the server's durationStats: a flat ms array in,
// {count, totalMs, avgMs, medianMs, p90Ms, maxMs} out.
function durationStats(msList) {
  const sorted = [...msList].sort((a, b) => a - b)
  const count = sorted.length
  const totalMs = sorted.reduce((sum, v) => sum + v, 0)
  return {
    count,
    totalMs,
    avgMs: count ? totalMs / count : 0,
    medianMs: percentileOf(sorted, 50),
    p90Ms: percentileOf(sorted, 90),
    maxMs: count ? sorted[count - 1] : 0,
  }
}

// Pools every selected agent's ms arrays for every day in `dates` into one
// flat list -- the shared first step both functions below build on.
function pooledMsList({ agentDaily, selectedAgents, dates }) {
  const selected = new Set(selectedAgents)
  const dateSet = new Set(dates)
  const pooled = []
  for (const a of agentDaily) {
    if (!selected.has(a.key)) continue
    for (const [day, msList] of Object.entries(a.points)) {
      if (dateSet.has(day)) pooled.push(...msList)
    }
  }
  return pooled
}

// Unbucketed stats over the whole displayed range, for the stat tiles --
// so deselecting a skewing agent changes the headline numbers, not just
// the chart line. count === 0 (nothing selected, or nothing in range for
// the selection) must be rendered as "no data" by the caller, never
// formatHandleMs(0) -- a real 0ms handle time and "nothing to measure"
// are different things and must not read the same.
export function pooledStats({ agentDaily, selectedAgents, dates }) {
  return durationStats(pooledMsList({ agentDaily, selectedAgents, dates }))
}

// dates: ['YYYY-MM-DD', ...] the full requested range, in order.
// agentDaily: [{ key, points: { 'YYYY-MM-DD': number[] } }] -- htData.agentDaily.
// granularity: 'day' | 'week'. selectedAgents: string[] (agent keys).
// Returns { bucketDates, series: [{key:'avg',points},{key:'median',points}] }
// in MINUTES (matching the existing series' unit) -- a bucket with zero
// pooled contacts gets no key at all (a gap, not a zero), same convention
// aggregateContactsPerHour already uses.
export function aggregateHandleTime({ dates, agentDaily, granularity, selectedAgents }) {
  const selected = new Set(selectedAgents)
  const byBucket = {}
  for (const a of agentDaily) {
    if (!selected.has(a.key)) continue
    for (const [day, msList] of Object.entries(a.points)) {
      if (!dates.includes(day)) continue
      const bucket = bucketOf(day, granularity)
      ;(byBucket[bucket] ??= []).push(...msList)
    }
  }

  const bucketDates = granularity === 'week'
    ? [...new Set(dates.map((d) => weekEndingOf(d)))].sort()
    : dates

  const medianPoints = {}
  const avgPoints = {}
  for (const b of bucketDates) {
    const pooled = byBucket[b]
    if (!pooled?.length) continue
    const s = durationStats(pooled)
    medianPoints[b] = s.medianMs / 60000
    avgPoints[b] = s.avgMs / 60000
  }

  return {
    bucketDates,
    series: [
      { key: 'avg', label: 'Average handle time', points: avgPoints },
      { key: 'median', label: 'Median handle time', points: medianPoints },
    ],
  }
}
